// @ts-strict-ignore
import React, { FC, useRef, useEffect, useState } from 'react'
import mapboxgl from 'mapbox-gl'
import { MapContainer, Marker } from '../types'
import { CollectionItem, ScreenSection, MapContainer as Container, RootStoreState } from 'mytours-read-common'
import { MapRoute } from 'mytours-read-api-typescript'
import { getCurrentLanguage, getStyleProperty } from 'mobile_web/modules/platform'
import { defaultMapStyle, formatMapPinIcon } from 'mobile_web/modules/map/functions'
import { generateMapPinIconUrl } from 'common_lib/maps/functions'
import Button from '../../common/components/Button'
import strings from '../../../strings'
import { useSelector } from 'react-redux'

interface OwnProps extends MapContainer {
	section: ScreenSection
	items: CollectionItem[]
	mapRoutes?: any[]
	onClick?: () => void
}

type Props = Container.Actions & Container.Props & OwnProps

const MapPreview: FC<Props> = ({ section, loadMapRoutes, items, mapRoutes, onClick }) => {
	const map = useRef<mapboxgl.Map>()
	const [markers, setMarkers] = useState<Marker[]>([])
	const mapContainer = useRef<HTMLDivElement>()
	const [mapContainerReady, setMapContainerReady] = useState(false)
	const [mapRoutesDrawn, setMapRoutesDrawn] = useState(false)
	const [markersCreated, setMarkersCreated] = useState(false)
	const [mapLoaded, setMapLoaded] = useState(false)

	const appData = useSelector((state: RootStoreState) => state.settings.appMetaData)

	useEffect(
		() => () => {
			map.current?.remove()
		},
		[],
	)

	const fitBounds = (): void => {
		const bounds = new mapboxgl.LngLatBounds()

		markers.map(marker => {
			bounds.extend(new mapboxgl.LngLat(marker.lng as number, marker.lat as number))
		})

		mapRoutes.forEach(route => {
			route.features.map(feat => {
				const coords = feat.geometry.coordinates
				if (Array.isArray(coords) && coords.length === 2 && typeof coords[0] == 'number' && typeof coords[1] == 'number') {
					bounds.extend(new mapboxgl.LngLat(coords[0], coords[1]))
				}
				else {
					feat.geometry.coordinates.map(coords => {
						bounds.extend(coords)
					})
				}
			})
		})
		if (!bounds.isEmpty()) {
			map.current.fitBounds(bounds, { padding: 30, duration: 0 })
		}
	}

	useEffect(() => {
		if (map?.current?.isStyleLoaded()) {
			setTimeout(() => {
				drawMapRoutes()
			}, 1000)
		}
	}, [mapRoutes, map?.current?.isStyleLoaded()])

	useEffect(() => {
		if (mapRoutesDrawn && markersCreated) {
			fitBounds()
		}
	}, [mapRoutesDrawn, markersCreated])

	const createMarker = (collectionItem: CollectionItem): Promise<Marker> =>
		new Promise(resolve => {
			const { lat, lng, id, item_number: itemNumber } = collectionItem

			let { map_pin_colour: mapPinColour, map_pin_style: mapPinStyle, map_pin_icon: mapPinIcon } = collectionItem

			if ('stock_icon_pin_colour' in collectionItem) {
				mapPinColour = (collectionItem as any).stock_icon_pin_colour
				mapPinIcon = (collectionItem as any).stock_icon_pin_icon
				mapPinStyle = (collectionItem as any).stock_icon_pin_style
			}

			const icon = formatMapPinIcon({
				mapPinIcon,
				itemNumber,
				keypadEnabled: appData.keypad_enabled,
				keypadDigits: appData.keypad_digits,
			})

			const el = document.createElement('div')
			el.className = 'custom-marker'
			el.style.backgroundImage = `url(${generateMapPinIconUrl({
				style: mapPinStyle,
				color: mapPinColour,
				icon,
			})})`

			const marker = new mapboxgl.Marker(el).setLngLat([Number(lng), Number(lat)]).addTo(map.current)

			resolve({
				marker,
				lat,
				lng,
				collectionItemId: id,
				pinColor: mapPinColour,
				pinIcon: icon,
				pinStyle: mapPinStyle,
			})
		})

	const createMarkers = async () => {
		const markers = []
		for await (const item of items) {
			const hasCoordinates = item && item.lat && item.lng

			if (hasCoordinates) {
				const marker = await createMarker(item)
				markers.push(marker)
			}
		}

		setMarkers(markers)
		setMarkersCreated(true)
	}

	useEffect(() => {
		if (mapLoaded && mapRoutes && mapRoutes.length > 0) {
			mapRoutes.forEach(route => {
				route.features.forEach(feat => {
					const { icon } = feat.properties
					if (!icon) return
					const [iconStyle, iconColor, iconIcon]: string[] = icon.split('_')
					const el = document.createElement('div')
					el.className = 'custom-marker'
					el.style.backgroundImage = `url(${generateMapPinIconUrl({
						style: iconStyle,
						color: iconColor.replace('#', ''),
						icon: iconIcon,
					})})`
					const [lng, lat] = feat.geometry.coordinates
					const marker = new mapboxgl.Marker(el).setLngLat([Number(lng), Number(lat)]).addTo(map.current)
				})
			})
		}
	}, [mapRoutes, mapLoaded])

	const drawMapRoutes = () => {
		if (mapRoutes && mapRoutes.length > 0) {
			mapRoutes.forEach((route, i) => {
				if (map.current.getLayer(`routeline${i}`)) {
					map.current.removeLayer(`routeline${i}`)
				}
				if (map.current.getSource(`route${i}`)) {
					map.current.removeSource(`route${i}`)
				}

				map.current.addSource(`route${i}`, {
					type: 'geojson',
					data: route,
				})

				map.current.addLayer({
					id: `routeline${i}`,
					type: 'line',
					source: `route${i}`,
					layout: {
						'line-join': 'round',
						'line-cap': 'round',
					},
					paint: {
						'line-color': route?.features?.[0]?.properties?.['line-color'] || getStyleProperty('--nav-bg'),
						'line-width': ['interpolate', ['linear'], ['zoom'], 12, 3, 22, 12],
					},
				})
			})
		}
		setMapRoutesDrawn(true)
	}

	const handleMapContainerReady = () => {
		if (mapContainer?.current) {
			const mapOptions: mapboxgl.MapboxOptions = {
				container: mapContainer.current,
				style: `mapbox://styles/${defaultMapStyle}`,
				zoom: 8,
				center: [0, 0],
				interactive: false,
			}

			map.current = new mapboxgl.Map(mapOptions)

			map.current.on('load', () => {
				createMarkers()
				setMapLoaded(true)
			})
		}
	}

	useEffect(() => {
		if (mapContainer?.current && mapContainer.current?.offsetWidth > 0 && mapContainer.current?.offsetHeight > 0) {
			setTimeout(() => map.current.resize(), 0)
		}
	}, [mapContainer?.current?.offsetWidth, mapContainer?.current?.offsetHeight])

	useEffect(() => {
		if (section) {
			console.log('section', section)
			loadMapRoutes(section, MapRoute.LinkedTypeEnum.StorySection, getCurrentLanguage())
		}
	}, [section])

	const renderExpandButton = (): JSX.Element => (
		<div className="map-controls-container">
			<div className="map-controls-row">
				<Button ariaLabel={strings.expand_map} onClick={onClick}>
					<i className="fal fa-arrow-up-right-and-arrow-down-left-from-center" />
				</Button>
			</div>
		</div>
	)

	return (
		<div
			id={`mapContainer-${section?.id}`}
			className="map-preview"
			onClick={onClick}
			ref={ref => {
				mapContainer.current = ref
				if (mapContainer.current && !mapContainerReady) {
					setMapContainerReady(true)
					handleMapContainerReady()
				}
			}}
			style={{ width: '100%', height: '300px' }}
		>
			{renderExpandButton()}
		</div>
	)
}

const MapPreviewComponent = Container.default(MapPreview)
export default MapPreviewComponent as any
